import { castError } from '@sketch/utils'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  StyledIconWrapper,
  StyledInput,
  InlineEditorWrapper,
} from './InlineEditorForm.styles'
import { Formik, FormikProps, FormikHelpers } from 'formik'
import { ErrorHandler } from '@sketch/tracing'
import { Icon } from '../MenuLink'
import { Tooltip, Form } from '@sketch/components'
import * as yup from 'yup'
import { FolderTerm, useFolderTerm } from '@sketch/modules-common'

interface FormValues {
  projectName: string
}

type FormActions = FormikHelpers<FormValues>

type FormOnSubmit = (
  formValues: FormValues,
  formikActions?: FormActions
) => Promise<any> | undefined

export interface InlineEditorFormFormProps {
  placeholder: string
  className?: string
  dataTestId?: string
  onSubmit: FormOnSubmit
  toggleEditor: () => void
  defaultValue?: string
  icon: {
    component: React.ElementType<any>
    label: string
  }
}

const inlineEditorFormSchema = (folderTerm: FolderTerm) =>
  yup.object({
    projectName: yup
      .string()
      .max(
        100,
        `Your ${folderTerm.lower} name cannot be longer than 100 characters`
      )
      .trim()
      .required(`A ${folderTerm.lower} name is required`),
  })

export const InlineEditorForm = ({
  placeholder,
  className,
  dataTestId,
  onSubmit,
  toggleEditor,
  defaultValue,
  icon,
}: InlineEditorFormFormProps) => {
  const [folderName, setFolderName] = useState(defaultValue || '')

  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => inputRef.current?.select(), [])

  const folderTerm = useFolderTerm()
  const schema = useMemo(() => inlineEditorFormSchema(folderTerm), [folderTerm])

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        toggleEditor()
      }
    }

    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [toggleEditor])

  const handleFormikRender = (formikBag: FormikProps<FormValues>) => {
    const { errors, values, handleBlur, handleChange, isSubmitting } = formikBag

    const submit = () => {
      const { isSubmitting, submitForm } = formikBag
      if (isSubmitting) return
      submitForm()
      setFolderName('')
    }

    const onBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (folderName.trim() === '' || folderName === undefined) {
        toggleEditor()
        return
      }

      if (!errors.projectName) {
        submit()
      }

      handleBlur(e)
    }

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFolderName(e.target.value)
      handleChange(e)
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        if (folderName === undefined || folderName.trim().length === 0) {
          toggleEditor()
          return
        }

        if (!errors) {
          submit()
        }
      }
    }

    return (
      <Form data-testid={dataTestId} className={className}>
        <Form.Field name="projectName">
          <Tooltip
            placement="bottom"
            spacing="8px"
            content={errors?.projectName}
            visible={!!errors?.projectName}
            disabled={!errors.projectName}
          >
            <InlineEditorWrapper aria-label="Inline Editor">
              <StyledIconWrapper>
                <Icon as={icon.component} aria-label={icon.label} />
              </StyledIconWrapper>
              <StyledInput
                ref={inputRef}
                name="projectName"
                type="text"
                placeholder={placeholder}
                value={values.projectName}
                onChange={onChange}
                onKeyPress={handleKeyPress}
                onBlur={onBlur}
                disabled={isSubmitting}
                hasError={errors?.projectName}
                autoFocus
              />
            </InlineEditorWrapper>
          </Tooltip>
        </Form.Field>
      </Form>
    )
  }

  const handleOnSubmit = async (
    values: FormValues,
    formikActions: FormActions
  ) => {
    const trimmedValues = schema.cast(values)

    formikActions.setSubmitting(true)

    try {
      // This is already being validated, but since yup.cast can return
      // undefined we need this extra check.
      const { projectName } = trimmedValues
      projectName && (await onSubmit({ projectName }, formikActions))
    } catch (e) {
      const error = castError(e)
      ErrorHandler.ignore(
        error,
        'Ignore this error the mutation will handle it'
      )
    } finally {
      formikActions.setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={{ projectName: folderName }}
      onSubmit={handleOnSubmit}
      validationSchema={schema}
    >
      {handleFormikRender}
    </Formik>
  )
}
